import { WSTable } from "@wingspanhq/fe-component-library";
import {
  PayeeRow,
  PayeeRowLinkRequestStatus,
  PayeeRowTaxInformationShare,
  PayeeStatus
} from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useMutationDownloadPayeesCSV } from "../../../../query/payee/mutations/useMutationDownloadPayeesCSV";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { useResendPayeeInvite } from "../../hooks/useResendPayeeInvite";
import { usePayeeRowActions } from "../../routes/RoutePayeeDetails/usePayeeRowActions";
import { useGoToPayeeDetails } from "../../utils";
import { getColumns } from "./columns";

export interface TablePayeesProps {
  payees: PayeeRow[];
  loading?: boolean;
  isArchivedOnly?: boolean;
}

export const TablePayees: React.FC<TablePayeesProps> = props => {
  const history = useHistory();
  const isOrganizationContext = !!useXWingspanExpansion();
  const goToPayeeDetails = useGoToPayeeDetails();
  const getRowActions = usePayeeRowActions();
  const [resendInvite] = useResendPayeeInvite();
  const [downloadCSV, downloadCSVMeta] = useMutationDownloadPayeesCSV();

  const columns = useMemo(
    () => getColumns(isOrganizationContext),
    [isOrganizationContext]
  );

  const tableData = useMemo(
    () => props.payees.map(payee => ({ id: payee.id ?? "", data: payee })),
    [props.payees]
  );

  return (
    <WSTable<PayeeRow>
      loading={props.loading}
      tableData={tableData}
      columns={columns}
      onRowClick={({ data }) => {
        if (data.id) {
          goToPayeeDetails(data.id);
        }
      }}
      headerAction={{
        icon: "download",
        onClick: async () => {
          // CSV export still runs on the v1 search service (a separate,
          // unmigrated concern); it exports every contractor and does not honor
          // the active tab/filter — see V3_MIGRATION_TRACKER.md (payee-rows §).
          await downloadCSV({
            fileName: props.isArchivedOnly
              ? "Archived contractors"
              : "Contractors"
          });
        },
        loading: downloadCSVMeta.isLoading
      }}
      rowActions={({ data }) =>
        data.linkRequestStatus === PayeeRowLinkRequestStatus.PENDING &&
        data.status !== PayeeStatus.DEACTIVATED
          ? [
              {
                text: "Resend invite",
                onAsyncClick: async () => {
                  if (data.id) {
                    await resendInvite(data.id);
                  }
                }
              }
            ]
          : []
      }
      rowMenuActions={({ data }) =>
        getRowActions({
          payee: {
            payeeId: data.id ?? "",
            email: data.email ?? undefined,
            // Internal notes aren't materialized on the search row; the notes
            // modal reads the current value itself when opened.
            internalNotes: undefined,
            isInactive: data.status === PayeeStatus.DEACTIVATED,
            isPending:
              data.linkRequestStatus === PayeeRowLinkRequestStatus.PENDING,
            isPayeeProvidedInfo:
              data.taxInformationShare === PayeeRowTaxInformationShare.ALLOW
          },
          backPath: history.location.pathname
        })
      }
    />
  );
};
